import { ArrowBack } from "@mui/icons-material";
import { Container, Grid, IconButton, Link, Stack } from "@mui/material";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import AcxDateRangeInput from "components/UI/Calendar/DateRange/AcxDateRangeInput";
import ChromeTabs, { ChromeTab } from "components/UI/SubTabs/ChromeTabs";
import { AmdTimelineWithGraph } from "components/UI/Timeline/AmdTimelineWithGraph";
import { observer } from "mobx-react";
import { Moment } from "moment";
import React, { useEffect, useState } from "react";
import { AuthStore } from "stores/AuthStore";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import FileProcessFailures from "./FileProcessFailures";
import { FileProcessStore, FileProcessView } from "./FileProcessStore";

const DateRangePicker = observer(
    ({ onSelect }: { onSelect: (start: Moment, end: Moment) => void }) => {
        const store = useStore(FileProcessStore);
        return (
            <Grid item container sx={{ paddingLeft: "8px" }}>
                <AcxDateRangeInput
                    labelText="Select Date"
                    defaultStartDate={store.datePickerStore.beginDate}
                    defaultEndDate={store.datePickerStore.endDate}
                    onSelect={onSelect}
                    innerStyles={{ height: "30px" }}
                />
            </Grid>
        );
    },
);

const GoBackButton = ({ onClick }: { onClick: () => void }) => (
    <Stack direction="row" alignItems="center" onClick={onClick}>
        <Link
            variant="body1"
            sx={{
                textAlign: "left",
                color: theme.palette.blue.main,
                fontWeight: "bolder",
                cursor: "pointer",
            }}
            color="textPrimary"
        >
            <IconButton size="medium">
                <ArrowBack />
            </IconButton>
            Go Back
        </Link>
    </Stack>
);

const DataGridGoBackButton = observer(() => {
    const store = useStore(FileProcessStore);

    return store.view !== FileProcessView.Dashboard ? (
        <GoBackButton onClick={() => store.goBackView()} />
    ) : null;
});

const DashboardContent = observer(({ activeTab }: { activeTab: number }) => {
    const store = useStore(FileProcessStore);

    return (
        <Container maxWidth="xl" sx={{ height: "100%" }}>
            {store.view === FileProcessView.AmdTimeline && (
                <Grid container justifyContent="flex-start">
                    <GoBackButton onClick={() => store.goBackView()} />
                </Grid>
            )}
            <Grid
                container
                item
                spacing={3}
                direction="column"
                alignItems="center"
                sx={{ height: "100%", padding: "2rem" }}
                wrap="nowrap"
            >
                {store.view !== FileProcessView.AmdTimeline && (
                    <AcxDataGrid dataGridStore={store.dataGridStore} />
                )}
                {store.view === FileProcessView.AmdTimeline &&
                    store.selectedAmd && (
                        <AmdTimelineWithGraph amdId={store.selectedAmd.id} />
                    )}
            </Grid>
        </Container>
    );
});

const FileProcessDashboard = observer(() => {
    const store = useStore(FileProcessStore);
    const authStore = useStore(AuthStore);
    const [activeTab, setActiveTab] = useState<number>(0);

    useEffect(() => {
        store.dataGridStore.rowTerm = <DataGridGoBackButton />;
        store.dataGridStore.controls = [
            <DateRangePicker
                onSelect={(start: Moment, end: Moment) => {
                    store.datePickerStore.setBeginDate(start);
                    store.datePickerStore.setEndDate(end);
                }}
            />,
        ];
        if (authStore.orgStore.selectedOrganization) {
            store.setOrganization(authStore.orgStore.selectedOrganization);
        }
    }, [authStore.orgStore.selectedOrganization, store]);

    const tabs: Array<ChromeTab> = [
        { label: "Dashboard", route: "/admin/fileprocessdashboard" },
        {
            label: "Processing Failures",
            route: "/admin/fileprocessdashboard/fileprocessfailures",
        },
    ];
    const handleTabChange = (index: number) => setActiveTab(index);

    return (
        <>
            <ChromeTabs
                tabs={tabs}
                activeTabIndex={activeTab}
                onChange={handleTabChange}
            />
            <div
                style={{
                    position: "relative",
                    width: "100%",
                    height: "calc(100% - 100px)",
                    overflow: "auto",
                }}
            >
                {activeTab === 0 ? (
                    <DashboardContent activeTab={activeTab} />
                ) : (
                    <FileProcessFailures />
                )}
            </div>
        </>
    );
});

export default FileProcessDashboard;
